import { useEffect, useState, useCallback } from "react";
import { Building2, Plus, Copy, Check, Trash2, Store, Users, BarChart3, Mail, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSuperAdmin } from "@/components/ProtectedRoute";
import { TopNav } from "@/components/TopNav";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const Inp = (props) => (
  <input {...props} className="w-full border border-slate-200 px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent" />
);
const Lbl = ({ children }) => (
  <span className="text-xs uppercase tracking-widest font-medium text-slate-500">{children}</span>
);

export default function FranchisePortal() {
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState("");

  useEffect(() => {
    if (superAdmin) {
      api.get("/admin/franchises").then(({ data }) => {
        setFranchises(data);
        if (data.length) setFranchiseId(data[0].id);
      }).catch(() => {});
    }
  }, [superAdmin]);

  const fq = useCallback((params = {}) => {
    if (superAdmin && franchiseId) return { ...params, franchise_id: franchiseId };
    return params;
  }, [superAdmin, franchiseId]);

  const ready = !superAdmin || !!franchiseId;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="franchise-portal">
      <TopNav />
      <div className="h-16 flex items-center justify-between px-8 bg-white border-b border-slate-200">
        <div>
          <h1 className="font-heading font-black text-xl tracking-tighter">Franchise Portal</h1>
          <p className="text-xs text-slate-500">Manage your stores and team</p>
        </div>
        {superAdmin && franchises.length > 0 && (
          <div className="w-64" data-testid="franchise-selector">
            <Select value={franchiseId} onValueChange={setFranchiseId}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="px-8 pt-6">
        <div className="flex gap-1 border-b border-slate-200">
          {[["dashboard", "Dashboard", BarChart3], ["stores", "Stores", Store], ["team", "Team", Users]].map(([k, label, Icon]) => (
            <button key={k} data-testid={`franchise-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-all ${tab === k ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-8 pb-8 pt-6">
        {!ready ? (
          <div className="text-center text-slate-500 py-12">No franchise selected.</div>
        ) : tab === "dashboard" ? (
          <FranchiseDashboard fq={fq} key={`d-${franchiseId}`} />
        ) : tab === "stores" ? (
          <StoresTab fq={fq} key={`s-${franchiseId}`} />
        ) : (
          <TeamTab fq={fq} key={`t-${franchiseId}`} />
        )}
      </div>
    </div>
  );
}

const KPI = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-widest font-medium text-slate-500">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${accent ? "text-blue-700" : ""}`}>{value}</div>
  </div>
);

function FranchiseDashboard({ fq }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/franchise/dashboard", { params: fq() }).then(({ data }) => setData(data)).catch(() => setData(null)); }, [fq]);
  if (!data) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" /></div>;
  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="franchise-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPI testid="kpi-leads" label="Leads" value={t.leads} accent />
        <KPI testid="kpi-upcoming" label="Upcoming" value={t.upcoming} accent />
        <KPI testid="kpi-overdue" label="Overdue" value={t.overdue} />
        <KPI testid="kpi-events" label="Events" value={t.events} />
        <KPI testid="kpi-catering" label="Catering" value={t.catering} />
        <KPI testid="kpi-pending" label="Pending Invites" value={`${data.users.pending}/${data.users.total}`} />
      </div>

      <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800 flex items-center gap-2" data-testid="activation-banner">
        <Mail className="h-4 w-4" />
        {data.users.total === 0
          ? "No team members invited yet — head to the Team tab to invite your store managers."
          : `${data.users.active} of ${data.users.total} team members have activated their accounts${data.users.pending ? ` — ${data.users.pending} invite(s) still pending.` : "."}`}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500">
              <th className="px-4 py-3 font-medium">Store</th>
              {["Leads", "Upcoming", "Overdue", "Events", "Catering"].map((h) => <th key={h} className="px-4 py-3 font-medium text-center">{h}</th>)}
            </tr>
          </thead>
          <tbody data-testid="per-store-table">
            {data.per_store.map((s) => (
              <tr key={s.store_code} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{s.name} <span className="text-xs text-slate-400 font-mono">#{s.store_code}</span></td>
                <td className="px-4 py-3 text-center">{s.leads}</td>
                <td className="px-4 py-3 text-center">{s.upcoming}</td>
                <td className={`px-4 py-3 text-center ${s.overdue ? "text-red-600 font-semibold" : ""}`}>{s.overdue}</td>
                <td className="px-4 py-3 text-center">{s.events}</td>
                <td className="px-4 py-3 text-center">{s.catering}</td>
              </tr>
            ))}
            {data.per_store.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No stores yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StoresTab({ fq }) {
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ store_code: "", name: "", address: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get("/stores", { params: fq() }).then(({ data }) => setStores(data)).catch(() => {}); }, [fq]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/stores", form, { params: fq() });
      toast.success("Store added");
      setOpen(false); setForm({ store_code: "", name: "", address: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const archive = async (id) => {
    try { await api.delete(`/stores/${id}`); toast.success("Store archived"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const restore = async (id) => {
    try { await api.patch(`/stores/${id}`, { status: "active" }); toast.success("Store restored"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="stores-tab">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-store-button" className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-none hover:bg-blue-800"><Plus className="h-4 w-4" /> Add Store</button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading tracking-tighter">Add a store location</DialogTitle><DialogDescription>Store numbers are unique across the platform.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <label className="block space-y-1.5"><Lbl>Store number</Lbl><Inp data-testid="store-code" value={form.store_code} onChange={(e) => setForm({ ...form, store_code: e.target.value })} placeholder="e.g. 18177" /></label>
              <label className="block space-y-1.5"><Lbl>Name (optional)</Lbl><Inp data-testid="store-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Store name" /></label>
              <label className="block space-y-1.5"><Lbl>Address (optional)</Lbl><Inp data-testid="store-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" /></label>
            </div>
            <DialogFooter>
              <button data-testid="submit-store" onClick={create} disabled={saving || !form.store_code} className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">{saving ? "Adding…" : "Add store"}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500"><th className="px-4 py-3 font-medium">Store #</th><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Address</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
          <tbody data-testid="stores-table">
            {stores.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`store-row-${s.id}`}>
                <td className="px-4 py-3 font-medium font-mono">{s.store_code}</td>
                <td className="px-4 py-3">{s.name}</td>
                <td className="px-4 py-3 text-slate-600">{s.address || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs border px-2 py-0.5 ${s.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{s.status}</span></td>
                <td className="px-4 py-3 text-right">
                  {s.status === "active"
                    ? <button data-testid={`archive-store-${s.id}`} onClick={() => archive(s.id)} title="Archive" className="text-slate-400 hover:text-red-600"><Archive className="h-4 w-4" /></button>
                    : <button data-testid={`restore-store-${s.id}`} onClick={() => restore(s.id)} title="Restore" className="inline-flex items-center gap-1.5 border border-emerald-200 text-emerald-700 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-600 hover:text-white transition-all"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>}
                </td>
              </tr>
            ))}
            {stores.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500" data-testid="stores-empty">No stores yet. Add your first location.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeamTab({ fq }) {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "store_user", assigned_stores: [] });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    api.get("/store-users", { params: fq() }).then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/stores", { params: fq() }).then(({ data }) => setStores(data.filter((s) => s.status === "active"))).catch(() => {});
  }, [fq]);
  useEffect(() => { load(); }, [load]);

  const toggleStore = (code) => setForm((f) => ({
    ...f, assigned_stores: f.assigned_stores.includes(code) ? f.assigned_stores.filter((c) => c !== code) : [...f.assigned_stores, code],
  }));

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/store-users", form, { params: fq() });
      setResult(data); toast.success(data.email_sent ? "Invite emailed" : "Invite created"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/store-users/${id}`); toast.success("User removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const reset = () => { setForm({ email: "", name: "", role: "store_user", assigned_stores: [] }); setResult(null); };
  const copy = () => { if (result?.invite_url) { navigator.clipboard.writeText(result.invite_url); setCopied(true); setTimeout(() => setCopied(false), 2000); } };

  return (
    <div data-testid="team-tab">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <button data-testid="add-team-user-button" className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-none hover:bg-blue-800"><Plus className="h-4 w-4" /> Invite User</button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading tracking-tighter">Invite a team member</DialogTitle><DialogDescription>Assign one or more stores. They'll set a password via the invite link.</DialogDescription></DialogHeader>
            {result ? (
              <div className="py-2" data-testid="team-invite-result">
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2 mb-4">{result.email_sent ? `Invite emailed to ${form.email}.` : "Invite created — copy the link below."}</div>
                <Lbl>Invite link</Lbl>
                <div className="flex items-center gap-2 mt-1.5">
                  <input data-testid="team-invite-link" readOnly value={result.invite_url} className="flex-1 border border-slate-200 px-3 py-2 text-xs font-mono bg-slate-50 rounded-none" />
                  <button onClick={copy} className="border border-slate-200 p-2 hover:bg-slate-900 hover:text-white transition-all">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
                </div>
                <DialogFooter className="mt-6"><button onClick={() => { setOpen(false); reset(); }} className="bg-slate-900 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-slate-800">Done</button></DialogFooter>
              </div>
            ) : (
              <>
                <div className="space-y-4 py-2">
                  <label className="block space-y-1.5"><Lbl>Full name</Lbl><Inp data-testid="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" /></label>
                  <label className="block space-y-1.5"><Lbl>Email</Lbl><Inp data-testid="team-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" /></label>
                  <label className="block space-y-1.5"><Lbl>Role</Lbl>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="team-role" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="store_user">Store User (GM / Marketing)</SelectItem>
                        <SelectItem value="franchisee_admin">Franchise Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                  <div className="space-y-1.5"><Lbl>Assigned stores</Lbl>
                    <div className="flex flex-wrap gap-2" data-testid="assign-stores">
                      {stores.map((s) => (
                        <button key={s.id} type="button" data-testid={`assign-store-${s.store_code}`} onClick={() => toggleStore(s.store_code)}
                          className={`text-xs border px-3 py-1.5 transition-all ${form.assigned_stores.includes(s.store_code) ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-400"}`}>
                          {s.store_code}
                        </button>
                      ))}
                      {stores.length === 0 && <span className="text-xs text-slate-400">Add stores first.</span>}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="submit-team-user" onClick={create} disabled={saving || !form.email || !form.name} className="bg-blue-700 text-white px-5 py-2 text-sm font-semibold rounded-none hover:bg-blue-800 disabled:opacity-50">{saving ? "Inviting…" : "Send invite"}</button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-500"><th className="px-4 py-3 font-medium">Name</th><th className="px-4 py-3 font-medium">Email</th><th className="px-4 py-3 font-medium">Role</th><th className="px-4 py-3 font-medium">Stores</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
          <tbody data-testid="team-table">
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`team-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs border border-slate-200 px-2 py-0.5">{u.role === "franchisee_admin" ? "Franchise Admin" : "Store User"}</span></td>
                <td className="px-4 py-3 text-slate-600">{(u.accessible_stores || []).join(", ") || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs border px-2 py-0.5 ${u.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{u.status}</span></td>
                <td className="px-4 py-3 text-right"><button data-testid={`delete-team-user-${u.id}`} onClick={() => remove(u.id)} title="Remove" className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="team-empty">No team members yet. Invite your store managers.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
